import { FormErrorMessage } from "@components/WAChatbutton/WAform/WidgetForm";
import { errMsgFocusStyle, inputStyle } from "@utils/form";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ClipLoader from "react-spinners/ClipLoader";

export interface TemplateGeneratorFormInputProps {
    businessName: string;
    businessBio: string;
    messageText: string;
    messageTone: string;
    addEmoji?: boolean;
    loadingState?: "generate" | "regenerate";
}

interface TemplateGeneratorFormProps {
    buttonText: string;
    onGenerate: (formData: TemplateGeneratorFormInputProps) => void;
    templateGeneratorRef: React.RefObject<HTMLDivElement>;
    errorMessage: boolean;
    isGeneratingTemplate: boolean;
}

const MessageToneInput: string[] = ["Punchy", "Informal", "Humorous", "Creative", "Minimal"];

const TemplateGeneratorForm: React.FC<TemplateGeneratorFormProps> = (props) => {
    const { buttonText, onGenerate, templateGeneratorRef, errorMessage, isGeneratingTemplate } = props;

    const {
        register,
        setValue,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<TemplateGeneratorFormInputProps>({
        defaultValues: {
            messageTone: "Punchy",
            addEmoji: true,
        },
    });

    useEffect(() => {
        const BusinessName = localStorage.getItem("businessName");
        const BusinessBio = localStorage.getItem("businessBio");

        if (BusinessName && BusinessBio) {
            setValue("businessName", BusinessName);
            setValue("businessBio", BusinessBio);
        }
    }, [setValue]);

    const onSubmit: SubmitHandler<TemplateGeneratorFormInputProps> = (formData) => {
        onGenerate({ ...formData, loadingState: "generate" });
        localStorage.setItem("businessName", formData.businessName);
        localStorage.setItem("businessBio", formData.businessBio);
    };

    return (
        <div ref={templateGeneratorRef} className="lg:w-1/2 w-fit lg:mx-0 mx-2 lg:px-5 lg:py-10 px-2 py-8 rounded-xl lg:shadow-lg shadow-2xl">
            <form id="gb_aiTemplateGenerator_form" onSubmit={handleSubmit(onSubmit)} className="lg:w-full w-fit flex flex-col items-center justify-center gap-10">
                <div className="flex items-center lg:gap-2.5 gap-4">
                    <div className="flex items-center justify-center bg-blue-100 rounded-full w-12 h-12" style={{ minWidth: "50px", minHeight: "50px" }}>
                        <Image width={30} height={30} src="/ai-whatsapp-template-generator/Form/bot-icon.png" alt="Bot logo" />
                    </div>
                    <h3 className="lg:text-2xl text-lg font-bold text-gray-700 lg:max-w-md">Let AI craft personalized messages for your business</h3>
                </div>
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                        <TemplateGeneratorFormLabel htmlFor="name">About your Business</TemplateGeneratorFormLabel>
                        <input
                            style={{ ...inputStyle, ...(errors.businessName?.message ? errMsgFocusStyle : {}) }}
                            id="businessName"
                            type="text"
                            placeholder="Give a brief description of your business"
                            className="text-base font-normal h-10"
                            {...register("businessName", {
                                required: {
                                    value: true,
                                    message: "Business Name is required",
                                },
                                minLength: {
                                    value: 3,
                                    message: "Business Name must be at least 3 characters",
                                },
                                maxLength: {
                                    value: 256,
                                    message: "Maximum limit exceeded",
                                },
                            })}
                        />
                        {errors.businessName && <FormErrorMessage>{errors.businessName.message}</FormErrorMessage>}
                    </div>
                    <div className="flex flex-col gap-2">
                        <TemplateGeneratorFormLabel htmlFor="name">Business Bio</TemplateGeneratorFormLabel>
                        <textarea
                            style={{ ...inputStyle, ...(errors.businessBio?.message ? errMsgFocusStyle : {}) }}
                            id="businessBio"
                            placeholder="About your business"
                            className="focus:outline-none text-base font-normal h-20"
                            {...register("businessBio", {
                                required: {
                                    value: true,
                                    message: "Business Bio is required",
                                },
                                minLength: {
                                    value: 3,
                                    message: "Business Bio must be at least 3 characters",
                                },
                                maxLength: {
                                    value: 256,
                                    message: "Maximum limit exceeded",
                                },
                            })}
                        />
                        {errors.businessBio && <FormErrorMessage>{errors.businessBio.message}</FormErrorMessage>}
                    </div>
                    <div className="flex flex-col gap-2">
                        <TemplateGeneratorFormLabel htmlFor="welcomeText">Enter your prompt</TemplateGeneratorFormLabel>
                        <textarea
                            style={{ ...inputStyle, ...(errors.messageText?.message ? errMsgFocusStyle : {}) }}
                            className="focus:outline-none text-base font-normal h-28"
                            id="messageText"
                            placeholder="Describe the message you want to convey"
                            {...register("messageText", {
                                required: { value: true, message: "Message is required" },
                                minLength: {
                                    value: 3,
                                    message: "Message must be at least 3 characters",
                                },
                                maxLength: {
                                    value: 1024,
                                    message: "Maximum limit exceeded",
                                },
                            })}
                        />
                        {errors.messageText && <FormErrorMessage>{errors.messageText.message}</FormErrorMessage>}
                    </div>
                    <div className="flex flex-col gap-2">
                        <TemplateGeneratorFormLabel htmlFor="messageTone">Message Tone</TemplateGeneratorFormLabel>
                        <Controller name="messageTone" control={control} rules={{ required: true }} render={({ field }) => <MessageTone handleCallback={field.onChange} />} />
                    </div>
                    <div className="flex items-center gap-4">
                        <TemplateGeneratorFormLabel htmlFor="addEmoji">Add Emoji 😎</TemplateGeneratorFormLabel>
                        <div className="rounded">
                            <label className="switch">
                                <input type="checkbox" {...register("addEmoji")} />
                                <span className="slider round"></span>
                            </label>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-2.5 items-center">
                    <button
                        form="gb_aiTemplateGenerator_form"
                        type="submit"
                        className="gb_ai_template_generator_btn focus:outline-none bg-blue-500 rounded-md px-6 py-2.5 text-lg text-white font-semibold w-fit hover:bg-blue-600 hover:shadow-md"
                        disabled={isGeneratingTemplate}
                    >
                        {isGeneratingTemplate ? (
                            <div className="flex gap-3 items-center justify-center">
                                <ClipLoader color={"#ffffff"} loading={true} size={20} />
                                <p>Generating</p>
                            </div>
                        ) : (
                            buttonText
                        )}
                    </button>
                    {errorMessage && <p className="text-base text-red-500 font-medium">* Something went wrong</p>}
                </div>
            </form>
        </div>
    );
};

export default TemplateGeneratorForm;
interface MessageToneProps {
    handleCallback: (data: string) => void;
}
const commonStyle = "flex items-center justify-center border-solid border lg:text-base text-sm rounded-md lg:p-2.5 p-1 hover:shadow-md";
const MessageTone: React.FC<MessageToneProps> = (props) => {
    const { handleCallback } = props;
    const [clickIndex, setClickIndex] = useState<number>(0);

    const defaultTone = (item: string, index: number) => {
        setClickIndex(index);
        handleCallback(item);
    };

    return (
        <div className="flex lg:gap-2.5 gap-1.5">
            {MessageToneInput.map((item, index) => (
                <div key={index}>
                    <button
                        type="button"
                        aria-label="pick message tone"
                        data-cy={`avatar ${index}`}
                        className={`${commonStyle} ${
                            clickIndex === index
                                ? "border-blue-500 bg-blue-400 text-white focus:outline-none ring-1"
                                : " border-gray-400 bg-gray-50 text-gray-500 focus:outline-none"
                        }`}
                        onClick={() => defaultTone(item, index)}
                    >
                        {item}
                    </button>
                </div>
            ))}
        </div>
    );
};
type TemplateGeneratorFormLabelProps = React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLLabelElement>, HTMLLabelElement>;
const TemplateGeneratorFormLabel: React.FC<TemplateGeneratorFormLabelProps> = (props) => {
    const { children, ...labelProps } = props;
    return (
        <label {...labelProps} className="text-base font-medium text-gray-700">
            {children}
        </label>
    );
};
